/**
 * Image URL input with optional file upload.
 * Either paste a URL or upload a file — both set the same imageUrl value used for tiles.
 */

import React, { useRef, useState } from 'react';
import { Upload, X } from 'lucide-react';
import garageAPI from '../../../services/garage/api';
import { formatApiError } from '../../../utils/formatApiError';

const ImageUrlOrUpload = ({
  value = '',
  onChange,
  disabled = false,
  label = 'Image URL (optional)',
  placeholder = 'https://...',
  helpText = 'Paste a URL or upload an image — either becomes the tile image.',
}) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const handleUploadClick = () => {
    if (disabled || uploading) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setUploadError('');
    setUploading(true);
    try {
      const result = await garageAPI.uploadImage(file);
      if (!result?.url) {
        throw new Error('Upload succeeded but no URL was returned');
      }
      onChange?.(result.url);
    } catch (error) {
      console.error('Image upload failed:', error);
      setUploadError(formatApiError(error, 'Failed to upload image.'));
    } finally {
      setUploading(false);
    }
  };

  const clearImage = () => {
    setUploadError('');
    onChange?.('');
  };

  return (
    <div className="garage-form-group">
      <label>{label}</label>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <input
          type="url"
          value={value}
          onChange={(e) => {
            setUploadError('');
            onChange?.(e.target.value);
          }}
          placeholder={placeholder}
          disabled={disabled || uploading}
          style={{ flex: 1 }}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <button
          type="button"
          className="garage-btn garage-btn-secondary"
          onClick={handleUploadClick}
          disabled={disabled || uploading}
          title="Upload image"
          style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
        >
          <Upload size={14} />
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
        {value ? (
          <button
            type="button"
            className="garage-btn garage-btn-secondary"
            onClick={clearImage}
            disabled={disabled || uploading}
            title="Clear image"
            style={{ padding: '8px 10px' }}
          >
            <X size={14} />
          </button>
        ) : null}
      </div>
      {helpText ? (
        <small style={{ display: 'block', marginTop: '4px', color: 'var(--garage-text-3)', fontSize: '13px' }}>
          {helpText}
        </small>
      ) : null}
      {uploadError ? (
        <small style={{ display: 'block', marginTop: '4px', color: 'var(--garage-red, #c62828)', fontSize: '13px' }}>
          {uploadError}
        </small>
      ) : null}
      {value ? (
        <div style={{ marginTop: '10px' }}>
          <img
            src={value}
            alt="Tile preview"
            style={{
              maxWidth: '160px',
              maxHeight: '100px',
              objectFit: 'cover',
              borderRadius: '8px',
              border: '1px solid var(--garage-border, #e0e0e0)',
            }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
            onLoad={(e) => {
              e.currentTarget.style.display = 'block';
            }}
          />
        </div>
      ) : null}
    </div>
  );
};

export default ImageUrlOrUpload;
